// ========= CAMPAIGNS S3 CONFIG =========
const CAMPAIGNS_BUCKET_NAME = process.env.REACT_APP_CAMPAIGN_MEDIA_BUCKET_NAME
const REGION = process.env.REACT_APP_AWS_REGION || "me-central-1"
const CAMPAIGN_PATH_NAME = process.env.REACT_APP_CAMPAIGN_PATH_NAME
const CAMPAIGN_FILE_PATH_NAME = process.env.REACT_APP_CAMPAIGN_FiILE_PATH_NAME

const getConfig = () => {
  return {
    bucketName: CAMPAIGNS_BUCKET_NAME,
    region: REGION,
    bucketUrl: `https://${CAMPAIGNS_BUCKET_NAME}.s3.${REGION}.amazonaws.com`,
  }
}

export const CAMPAIGNS_S3_CONFIG = new Proxy(
  {},
  {
    get(target, prop) {
      const config = getConfig()
      return config[prop]
    },
  },
)
const pad = (n) => String(n).padStart(2, "0")

export const generateTimestampId = () => {
  const d = new Date()
  // Format: YYYYMMDDHHmmssSSS + random 2 digits
  // Example: 20260119202543523 (17 digits: YYYYMMDDHHmmssSSS)
  const timestamp =
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}` +
    String(d.getMilliseconds()).padStart(3, "0")
  // Add random 2 digits for uniqueness
  const random = String(Math.floor(Math.random() * 100)).padStart(2, "0")
  return timestamp + random
}

export const getFileExtension = (filename, fallback = "jpg") => {
  const lastDot = (filename || "").lastIndexOf(".")
  return lastDot === -1 ? fallback : filename.slice(lastDot + 1).toLowerCase()
}

export const generateUniqueFileName = (file) => {
  const uniqueSuffix = Math.random().toString(36).slice(2, 10)
  return `${generateTimestampId()}-${uniqueSuffix}.${getFileExtension(file?.name, "jpg")}`
}

export const getCampaignMediaPath = () => {
  return CAMPAIGN_PATH_NAME
}

// ========= VALIDATION =========
export const validateImageFile = (file, maxSizeMB = 10) => {
  if (!file) throw new Error("No file provided")
  if (!file.type?.startsWith("image/"))
    throw new Error("Only image files are allowed")
  if (file.size > maxSizeMB * 1024 * 1024)
    throw new Error(`File size must be less than ${maxSizeMB}MB`)
  return true
}

export const validateExcelFile = (file, maxSizeMB = 10) => {
  if (!file) throw new Error("No file provided")
  const validTypes = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "text/csv",
  ]
  const validExtensions = [".xls", ".xlsx", ".xlsm", ".csv"]
  const fileExtension = getFileExtension(file.name, "")

  const isValidType =
    validTypes.includes(file.type) ||
    validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))

  if (!isValidType) {
    throw new Error("Only Excel files (.xls, .xlsx, .xlsm, .csv) are allowed")
  }
  if (file.size > maxSizeMB * 1024 * 1024) {
    throw new Error(`File size must be less than ${maxSizeMB}MB`)
  }
  return true
}

const buildCampaignMediaKey = (fileName) => {
  const folderPrefix = String(getCampaignMediaPath() || "")
    .replace(/^\//, "")
    .replace(/\/$/, "")
  return folderPrefix ? `${folderPrefix}/${fileName}` : fileName
}

export async function uploadCampaignFileToS3(file) {
  validateImageFile(file, 10) // 10MB max

  const fileName = generateUniqueFileName(file)
  const fileKey = buildCampaignMediaKey(fileName)
  const uploadUrl = `${CAMPAIGNS_S3_CONFIG.bucketUrl}/${fileKey}`

  let resp
  try {
    resp = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type || "image/jpeg" },
    })
  } catch (error) {
    const isNetworkError =
      error?.name === "TypeError" ||
      String(error?.message || "").toLowerCase().includes("failed to fetch")
    throw new Error(
      isNetworkError
        ? "Upload failed due to a network error. Please try again."
        : error?.message || "Upload failed",
    )
  }

  if (!resp.ok) {
    const txt = await resp.text().catch(() => "")
    throw new Error(`Upload failed ${resp.status}: ${txt || resp.statusText}`)
  }

  return { key: fileKey, url: getCampaignPublicUrl(fileKey) }
}

export const getCampaignPublicUrl = (key) => {
  const config = getConfig()
  // Ensure key starts with / if it doesn't already
  const normalizedKey = key.startsWith("/") ? key : `/${key}`
  return `${config.bucketUrl}${normalizedKey}`
}

// ========= EXCEL FILE UPLOAD =========
export async function uploadCampaignExcelToS3(file, options = {}) {
  validateExcelFile(file)

  if (options?.existingKey) {
    const normalizedExistingKey = String(options.existingKey).replace(/^\//, "")
    const uploadUrl = `${CAMPAIGNS_S3_CONFIG.bucketUrl}/${normalizedExistingKey}`

    const resp = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type":
          file.type ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
    })

    if (!resp.ok) {
      const txt = await resp.text().catch(() => "")
      throw new Error(`Upload failed ${resp.status}: ${txt || resp.statusText}`)
    }

    return { key: normalizedExistingKey, url: getCampaignPublicUrl(normalizedExistingKey) }
  }

  // Generate timestamp for filename
  const timestamp = generateTimestampId()

  // Get file extension
  const fileExtension = getFileExtension(file.name, "xlsx")

  // Build S3 key path using environment variable
  // Remove leading slash if present to ensure consistent path format
  const folderPrefix = CAMPAIGN_FILE_PATH_NAME?.replace(/^\//, "") || "campaign_files_stage"
  const fileKey = `${folderPrefix}/${timestamp}.${fileExtension}`

  const uploadUrl = `${CAMPAIGNS_S3_CONFIG.bucketUrl}/${fileKey}`

  const resp = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type":
        file.type ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  })

  if (!resp.ok) {
    const txt = await resp.text().catch(() => "")
    throw new Error(`Upload failed ${resp.status}: ${txt || resp.statusText}`)
  }

  return { key: fileKey, url: getCampaignPublicUrl(fileKey) }
}
